import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Banknote, CreditCard, Plus, RefreshCw, Save, Search,
  Percent, Printer, Settings, ShoppingCart, Tag, Trash2, UserPlus, X, Zap
} from "lucide-react";
import { AuthContext } from "../App";
import { FacturaDocModal } from "./VentasPage";
import EmpresaSwitcher from "../components/EmpresaSwitcher";
import ProductoSearchSelect from "../components/ProductoSearchSelect";
import RecetaFormModal from "../components/RecetaFormModal";
import MedioCobroSelector, { useProcesadoras, comisionPreview } from "../components/MedioCobroSelector";
import { imprimirRecetaDeFactura, verificarControlados } from "../lib/recetaPrint";
import { svgDocumentHeaderLogoHtml } from "../lib/marcaLogoSvg";
import useDepositos from "../hooks/useDepositos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const today = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const fmtPYG = (n) => `₲ ${Math.round(Number(n || 0)).toLocaleString("es-PY")}`;
const fmtMonto = (n, moneda = "PYG") => {
  const value = Number(n || 0);
  if (moneda === "PYG") return fmtPYG(value);
  if (moneda === "BRL") return `R$ ${value.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${moneda} ${value.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const numberOnly = (value) => String(value || "").replace(/\D/g, "");
// Cantidad con decimales: acepta coma o punto, hasta 3 decimales (ej. 0,5 / 1.250)
const decimalQty = (value) => {
  let v = String(value ?? "").replace(",", ".").replace(/[^\d.]/g, "");
  const i = v.indexOf(".");
  if (i >= 0) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, "").slice(0, 3);
  return v;
};
const qtyNum = (value) => { const n = Number(String(value ?? "").replace(",", ".")); return Number.isFinite(n) && n > 0 ? n : 0; };
const moneyValue = (value, moneda = "PYG") => {
  if (moneda === "PYG") return Number(numberOnly(value) || 0);
  const clean = String(value || "").replace(/[^\d.,]/g, "").replace(",", ".");
  return Number(clean || 0);
};
const clampPct = (value) => Math.max(0, Math.min(100, Number(value || 0) || 0));

const emptyConfig = (logo) => ({
  logo_tipo: logo,
  cliente_default_id: "",
  usuarios: {},
  monedas_equivalencia: ["USD", "BRL"],
  impresion_modo: "global",       // "global" | "por_usuario" | "preguntar"
  impresion_default: "ticket",    // "ticket" | "a4"
});

function userDisplayName(u) {
  return u?.name || u?.email || u?.id || "Usuario";
}

// Monto en letras (guaraníes / otra moneda) — usado en la impresión A4
function numToLetras(n) {
  const num = Math.round(Math.abs(n || 0));
  if (num === 0) return "Cero";
  const un = ["", "un", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete", "dieciocho", "diecinueve"];
  const dec = ["", "", "veinte", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
  const cen = ["", "cien", "doscientos", "trescientos", "cuatrocientos", "quinientos", "seiscientos", "setecientos", "ochocientos", "novecientos"];
  const g = (x) => {
    if (x < 20) return un[x];
    const d = Math.floor(x / 10), u = x % 10;
    if (x < 100) return dec[d] + (u ? " y " + un[u] : "");
    const c2 = Math.floor(x / 100), r = x % 100;
    return (c2 === 1 && r > 0 ? "ciento " : cen[c2] + (r ? " " : "")) + (r ? g(r) : "");
  };
  const mil = (x) => x === 0 ? "" : x === 1 ? "un mil " : g(x) + " mil ";
  const m = Math.floor(num / 1000000), rest = num % 1000000;
  const k = Math.floor(rest / 1000), u2 = rest % 1000;
  let r = (m > 0 ? (m === 1 ? "un millón " : g(m) + " millones ") : "") + mil(k) + (u2 > 0 ? g(u2) : "");
  return r.trim().charAt(0).toUpperCase() + r.trim().slice(1);
}

// ─── Buscador de clientes con creación inline ─────────────────────────────────
function ClienteSearchSelect({ clientes, value, onChange, defaultLabel, onCrearNuevo }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return clientes.slice(0, 12);
    const q = query.toLowerCase();
    return clientes.filter(c =>
      (c.nombre || "").toLowerCase().includes(q) ||
      (c.razon_social || "").toLowerCase().includes(q) ||
      (c.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [clientes, query]);

  const selected = clientes.find(c => c.id === value || c.empresa_id === value);

  if (selected) {
    return (
      <div className="flex items-center gap-2 bg-slate-950 border border-amber-400/30 rounded-lg px-3 py-2.5 min-h-[42px]">
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm font-medium truncate block">{selected.razon_social || selected.nombre}</span>
          {selected.ruc && <span className="text-slate-400 text-xs">RUC: {selected.ruc}</span>}
        </div>
        <button type="button" onClick={() => onChange("")} className="text-slate-500 hover:text-white shrink-0 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={defaultLabel ? `Buscar cliente... (predeterminado: ${defaultLabel})` : "Buscar cliente por nombre o RUC..."}
          className="w-full bg-slate-950 border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50 placeholder:text-slate-500"
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-white/10 rounded-lg shadow-xl overflow-hidden">
          {filtered.length > 0 && (
            <ul className="max-h-56 overflow-y-auto divide-y divide-white/5">
              {filtered.map(c => (
                <li key={c.id} onMouseDown={() => { onChange(c.id); setQuery(""); setOpen(false); }}
                  className="px-3 py-2.5 hover:bg-white/5 cursor-pointer flex items-center justify-between gap-2">
                  <span className="text-white text-sm truncate">{c.razon_social || c.nombre}</span>
                  {c.ruc && <span className="text-slate-500 text-xs ml-2 shrink-0">RUC: {c.ruc}</span>}
                </li>
              ))}
            </ul>
          )}
          {filtered.length === 0 && query.trim() && (
            <div className="px-3 py-2 text-slate-500 text-xs">Sin resultados para "{query.trim()}"</div>
          )}
          {query.trim() && (
            <div className={filtered.length > 0 ? "border-t border-white/10" : ""}>
              <button type="button" onMouseDown={() => { onCrearNuevo?.(query.trim()); setQuery(""); setOpen(false); }}
                className="w-full px-3 py-2.5 text-left text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors">
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Crear "<strong>{query.trim()}</strong>" como nuevo cliente
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PDVPage() {
  const { token, user, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const inputRef = useRef(null);
  const logoTipo = activeEmpresaPropia?.slug || "arandujar";
  const depPdv = useDepositos(token, logoTipo);
  const [depositoVenta, setDepositoVenta] = useState("");
  const userId = user?.id;

  const [tab, setTab] = useState("operacion");
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [facturando, setFacturando] = useState(false);
  const [barcode, setBarcode] = useState("");
  const [cantidad, setCantidad] = useState("1");
  const [varianteScanPending, setVarianteScanPending] = useState(null); // { prod, variantesActivas, qty }
  const [condicion, setCondicion] = useState("contado");
  const [clienteId, setClienteId] = useState("");
  const [numeroManual, setNumeroManual] = useState("");
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [soloConStock, setSoloConStock] = useState(() => { try { return localStorage.getItem("pdv_solo_con_stock") === "1"; } catch { return false; } });
  const [productoSeleccionadoId, setProductoSeleccionadoId] = useState("");
  const [cuentas, setCuentas] = useState([]);
  const [planCuentas, setPlanCuentas] = useState([]);
  const [timbrado, setTimbrado] = useState(null);
  const [config, setConfig] = useState(emptyConfig(logoTipo));
  const [usuarios, setUsuarios] = useState([]);
  const [items, setItems] = useState([]);
  const [pagaCon, setPagaCon] = useState("");
  const [medioCobro, setMedioCobro] = useState({ procesadora_id: "", metodo_cobro: "" });
  const procesadoras = useProcesadoras(API, token, activeEmpresaPropia?.slug);
  const [moneda, setMoneda] = useState("PYG");
  const [cotizaciones, setCotizaciones] = useState({});
  const [clienteRapido, setClienteRapido] = useState({ nombre: "", ruc: "", telefono: "" });
  const [showClienteRapido, setShowClienteRapido] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cuentaContadoManualId, setCuentaContadoManualId] = useState("");
  const [descuentoModo, setDescuentoModo] = useState("none");       // "none" | "global"
  const [descuentoGlobalTipo, setDescuentoGlobalTipo] = useState("monto"); // "monto" | "pct"
  const [descuentoGlobalMonto, setDescuentoGlobalMonto] = useState("");
  const [descuentoGlobalPct, setDescuentoGlobalPct] = useState("");
  const [docPrint, setDocPrint] = useState(null); // { factura, autoPrint } -> modelo A4 oficial de Facturas
  const [pendingPrint, setPendingPrint] = useState(null); // { factura, resumen } cuando el modo es "preguntar"
  const [comandaAbierta, setComandaAbierta] = useState(null); // { id, mesa_nombre, numero } — tarjeta Restaurante cargada al carrito

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const jsonHeaders = useMemo(() => ({ ...headers, "Content-Type": "application/json" }), [headers]);
  const canConfig = hasPermission("pdv.configurar");
  const canOperar = hasPermission("pdv.operar") && hasPermission("facturas.crear");
  const canCrearCliente = hasPermission("entidades.crear") || hasPermission("empresas.crear");

  const userCfg = (config.usuarios || {})[userId] || {};
  // Cada punto de expedición tiene ahora su propio timbrado/modo_numeracion.
  const puntos = timbrado?.puntos_expedicion || [];
  const puntoCfgValido = puntos.some(p => p.codigo === userCfg.punto_expedicion) ? userCfg.punto_expedicion : "";
  const puntoActual = puntoCfgValido || timbrado?.punto_sugerido || timbrado?.punto_default || puntos[0]?.codigo || "001";
  const puntoActualData = puntos.find(p => p.codigo === puntoActual) || null;
  const esAutoNumerico = puntoActualData?.modo_numeracion === "automatico";
  const monedasHabilitadas = ["PYG", ...((activeEmpresaPropia?.monedas_habilitadas || []).filter(m => m && m !== "PYG"))];
  const tcActual = moneda === "PYG" ? 1 : Number(cotizaciones?.[moneda]?.tipo_cambio_sugerido || cotizaciones?.[moneda]?.venta || 0);
  const cuentaCfg = cuentas.find(c => c.id === userCfg.cuenta_contado_id && c.activa !== false);
  const cuentaContadoAuto = (cuentaCfg && cuentaCfg.moneda === moneda ? cuentaCfg.id : "") || cuentas.find(c => c.moneda === moneda && c.es_predeterminada)?.id || cuentas.find(c => c.moneda === moneda)?.id || cuentas.find(c => c.moneda === "PYG" && c.es_predeterminada)?.id || cuentas.find(c => c.moneda === "PYG")?.id || "";
  const cuentaContado = cuentaContadoManualId || cuentaContadoAuto;
  const planCredito = userCfg.plan_cuenta_credito_id || planCuentas.find(p => p.uso === "venta_credito" && p.activa !== false)?.id || "";
  const clienteSeleccionado = clientes.find(c => c.id === (clienteId || config.cliente_default_id) || c.empresa_id === (clienteId || config.cliente_default_id));

  const subtotalBrutoPyg = items.reduce((sum, item) => sum + (Number(item.precio_venta || 0) * qtyNum(item.cantidad)), 0);
  const totalDescuentosItemPyg = items.reduce((sum, item) => {
    const bruto = Number(item.precio_venta || 0) * qtyNum(item.cantidad);
    return sum + (bruto * clampPct(item.descuento_pct) / 100);
  }, 0);
  const netoTrasItemsPyg = Math.max(0, subtotalBrutoPyg - totalDescuentosItemPyg);

  const toDoc = useCallback((pyg) => {
    const value = Number(pyg || 0);
    if (moneda === "PYG") return Math.round(value);
    return tcActual > 0 ? Math.round((value / tcActual) * 100) / 100 : 0;
  }, [moneda, tcActual]);
  const fromDoc = useCallback((docAmount) => {
    const value = Number(docAmount || 0);
    if (moneda === "PYG") return Math.round(value);
    return tcActual > 0 ? Math.round(value * tcActual) : 0;
  }, [moneda, tcActual]);
  const pygToMoneda = useCallback((pyg, target) => {
    if (target === "PYG") return Math.round(Number(pyg || 0));
    const tc = Number(cotizaciones?.[target]?.tipo_cambio_sugerido || cotizaciones?.[target]?.venta || 0);
    return tc > 0 ? Math.round((Number(pyg || 0) / tc) * 100) / 100 : null;
  }, [cotizaciones]);
  // Inverso de pygToMoneda — convierte un monto en la moneda propia del producto (cargada en
  // el catálogo) a ₲, para que el carrito del PDV siga trabajando siempre en PYG por dentro.
  const monedaToPyg = useCallback((amount, monedaOrigen) => {
    const value = Number(amount || 0);
    if (!monedaOrigen || monedaOrigen === "PYG") return Math.round(value);
    const tc = Number(cotizaciones?.[monedaOrigen]?.tipo_cambio_sugerido || cotizaciones?.[monedaOrigen]?.venta || 0);
    return tc > 0 ? Math.round(value * tc) : null;
  }, [cotizaciones]);

  const descuentoGlobalPyg = descuentoModo === "global"
    ? (descuentoGlobalTipo === "pct"
        ? netoTrasItemsPyg * clampPct(descuentoGlobalPct) / 100
        : fromDoc(moneyValue(descuentoGlobalMonto, moneda)))
    : 0;
  const totalDescuentosPyg = totalDescuentosItemPyg + descuentoGlobalPyg;
  const totalPyg = Math.max(0, subtotalBrutoPyg - totalDescuentosPyg);
  const totalDoc = toDoc(totalPyg);
  const descuentoDoc = toDoc(totalDescuentosPyg);
  const efectivo = moneyValue(pagaCon, moneda);
  const comisionPago = condicion === "contado" ? comisionPreview(procesadoras, medioCobro, totalDoc).comision : 0;
  const vuelto = condicion === "contado" ? Math.max(efectivo - totalDoc, 0) : 0;

  // Nota: la referencia de equivalencia NO depende de "monedas habilitadas para
  // facturar" (eso es otra cosa — moneda en la que se puede EMITIR el documento).
  // Esto es solo una conversión informativa junto al total/vuelto, así que se
  // muestra siempre que esté configurada y haya cotización, sin importar si la
  // empresa factura o no en esa moneda ni qué moneda tenga elegida el selector.
  const monedasEquivalenciaConfig = config.monedas_equivalencia != null ? config.monedas_equivalencia : ["USD", "BRL"];
  const monedasEquivalencia = monedasEquivalenciaConfig.filter(m => m !== moneda);
  const equivalenciasTotal = monedasEquivalencia.map(m => ({ moneda: m, valor: pygToMoneda(totalPyg, m) })).filter(e => e.valor != null);
  const equivalenciasVuelto = monedasEquivalencia.map(m => ({ moneda: m, valor: pygToMoneda(fromDoc(vuelto), m) })).filter(e => e.valor != null);

  const loadData = useCallback(async () => {
    if (!token || !logoTipo) return;
    setLoading(true);
    try {
      const qLogo = `?logo_tipo=${encodeURIComponent(logoTipo)}`;
      const [rClientes, rCuentas, rPlan, rTimbrado, rConfig, rCotiz, rProductos] = await Promise.all([
        fetch(`${API}/admin/pdv/clientes${qLogo}`, { headers }),
        fetch(`${API}/admin/cuentas-bancarias${qLogo}`, { headers }),
        fetch(`${API}/admin/plan-cuentas${qLogo}`, { headers }),
        fetch(`${API}/admin/timbrado-vigente/${logoTipo}`, { headers }),
        fetch(`${API}/admin/pdv/config/${logoTipo}`, { headers }),
        fetch(`${API}/admin/pdv/cotizaciones${qLogo}`, { headers }),
        fetch(`${API}/admin/productos${qLogo}`, { headers }),
      ]);
      if (rClientes.ok) setClientes(await rClientes.json());
      if (rCuentas.ok) setCuentas(await rCuentas.json());
      if (rProductos.ok) { const d = await rProductos.json(); setProductos(Array.isArray(d) ? d : []); }
      if (rPlan.ok) setPlanCuentas(await rPlan.json());
      if (rTimbrado.ok) setTimbrado(await rTimbrado.json());
      if (rConfig.ok) setConfig({ ...emptyConfig(logoTipo), ...(await rConfig.json()) });
      if (rCotiz.ok) setCotizaciones((await rCotiz.json()).cotizaciones || {});
      if (canConfig) {
        const rUsers = await fetch(`${API}/admin/pdv/usuarios-configurables?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
        if (rUsers.ok) setUsuarios(await rUsers.json());
      }
    } catch (e) {
      toast.error("No se pudo cargar PDV");
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [token, logoTipo, headers, canConfig]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    setConfig(emptyConfig(logoTipo)); setItems([]); setClienteId(""); setMoneda("PYG");
    setCuentaContadoManualId(""); setMedioCobro({ procesadora_id: "", metodo_cobro: "" }); setDescuentoModo("none"); setDescuentoGlobalMonto(""); setDescuentoGlobalPct("");
  }, [logoTipo]);
  useEffect(() => {
    if (!showConfirm) return;
    const onKey = (e) => {
      if (e.key === "Enter" && !facturando) facturar();
      if (e.key === "Escape" && !facturando) setShowConfirm(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showConfirm, facturando]); // eslint-disable-line react-hooks/exhaustive-deps

  // Agrega un producto ya resuelto (venga de escaneo por código de barras o de
  // selección manual en el buscador) al carrito. El carrito trabaja siempre en
  // ₲ por dentro — si el producto está cargado en el catálogo en otra moneda
  // (USD/BRL), se convierte a ₲ acá usando la cotización del día.
  const addProductoAlCarrito = (prod, qty, variante = null) => {
    const monedaProd = prod.moneda || "PYG";
    const precioVentaBase = variante?.precio_venta ?? prod.precio_venta ?? prod.precio ?? 0;
    const precioCostoBase = variante?.precio_costo ?? prod.precio_costo ?? 0;
    const precioVentaPyg = monedaToPyg(precioVentaBase, monedaProd);
    const precioCostoPyg = monedaToPyg(precioCostoBase, monedaProd);
    if (precioVentaPyg === null) {
      toast.error(`No hay cotización de ${monedaProd} cargada — no se puede convertir el precio a ₲`);
      return false;
    }
    const etiqueta = variante?.atributos_texto || "";
    setItems(prev => {
      const idx = prev.findIndex(x => x.id === prod.id && String(x.variante_id || "") === String(variante?.id || ""));
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], cantidad: qtyNum(copy[idx].cantidad) + qty };
        return copy;
      }
      return [...prev, {
        id: prod.id,
        codigo_barras: prod.codigo_barras,
        nombre: prod.nombre || prod.descripcion,
        sku: prod.sku || "",
        variante_id: variante?.id || null,
        variante_texto: etiqueta || null,
        precio_venta: precioVentaPyg,
        precio_costo: precioCostoPyg || 0,
        moneda_producto: monedaProd !== "PYG" ? monedaProd : null,
        iva_tipo: prod.iva_tipo || "10",
        cantidad: qty,
        descuento_pct: 0,
      }];
    });
    const nombreBase = prod.nombre || prod.descripcion;
    toast.success(`${nombreBase}${etiqueta ? ` (${etiqueta})` : ""} agregado${monedaProd !== "PYG" ? ` (convertido de ${monedaProd})` : ""}`);
    return true;
  };

  // Confirma la variante elegida en el selector rápido (venga de escaneo o de
  // selección manual en el buscador de catálogo).
  const handleVarianteScanSelect = (variante) => {
    if (!varianteScanPending) return;
    const { prod, qty } = varianteScanPending;
    if (addProductoAlCarrito(prod, qty, variante)) {
      setBarcode("");
      setCantidad("1");
    }
    setVarianteScanPending(null);
  };

  // Carga el consumo pendiente de una tarjeta de comanda (módulo Restaurante)
  // al carrito del PDV. Los ítems de la comanda ya vienen con su nombre/precio/
  // moneda propios (no dependen del catálogo local), así que se convierten a ₲
  // igual que un producto normal y se agregan directo al carrito.
  const cargarComandaEnCarrito = (comanda) => {
    const itemsComanda = comanda.items || [];
    if (itemsComanda.length === 0) {
      toast.error("Esta tarjeta no tiene consumo cargado");
      return false;
    }
    let ok = true;
    itemsComanda.forEach(it => {
      const monedaItem = it.moneda_producto || "PYG";
      const precioPyg = monedaToPyg(it.precio_unitario, monedaItem);
      if (precioPyg === null) {
        toast.error(`No hay cotización de ${monedaItem} para "${it.nombre}"`);
        ok = false;
        return;
      }
      setItems(prev => [...prev, {
        id: it.producto_id || it.id,
        codigo_barras: it.codigo_barras || "",
        nombre: it.nombre,
        sku: "",
        variante_id: null,
        variante_texto: null,
        precio_venta: precioPyg,
        precio_costo: 0,
        moneda_producto: monedaItem !== "PYG" ? monedaItem : null,
        iva_tipo: "10",
        cantidad: (qtyNum(it.cantidad) || 1),
        descuento_pct: 0,
      }]);
    });
    if (ok) {
      setComandaAbierta({ id: comanda.id, mesa_nombre: comanda.mesa_nombre || "", numero: comanda.numero });
      toast.success(`Comanda ${comanda.mesa_nombre || comanda.numero} cargada al carrito`);
    }
    return ok;
  };

  const addProductByBarcode = async () => {
    const code = barcode.trim();
    const qty = qtyNum(cantidad) || 1;
    if (!code) return;
    try {
      // Primero probamos si el código escaneado es una tarjeta de comanda del
      // módulo Restaurante — si no existe, cae sin romper nada al flujo normal
      // de búsqueda de producto (404 esperado y silencioso).
      try {
        const rComanda = await fetch(`${API}/admin/restaurante/comandas/by-barcode/${encodeURIComponent(code)}?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
        if (rComanda.ok) {
          const comanda = await rComanda.json();
          if (cargarComandaEnCarrito(comanda)) {
            setBarcode("");
          }
          return;
        }
      } catch (_e) {
        // Restaurante no habilitado o error de red al consultar comanda: seguimos con producto normal.
      }
      const res = await fetch(`${API}/admin/productos/by-barcode/${encodeURIComponent(code)}?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Producto no encontrado");
      const prod = await res.json();
      const variantesActivas = (prod.variantes || []).filter(v => v.activo !== false);
      // Mismo código de barras con más de una variante activa (ej. mismo
      // modelo, distintos talles/colores): no podemos adivinar cuál es,
      // mostramos un selector rápido para que el usuario elija.
      if (prod.variantes_habilitadas && variantesActivas.length > 1) {
        setVarianteScanPending({ prod, variantesActivas, qty });
        return;
      }
      const varianteMatch = prod.variantes_habilitadas && variantesActivas.length === 1 ? variantesActivas[0] : null;
      if (addProductoAlCarrito(prod, qty, varianteMatch)) {
        setBarcode("");
        setCantidad("1");
      }
    } catch (e) {
      toast.error(e.message || "No se encontró el producto");
    } finally {
      inputRef.current?.focus();
    }
  };

  // Selección manual desde el buscador (no requiere código de barras).
  const addProductoSeleccionado = (id, prod) => {
    if (!prod) { setProductoSeleccionadoId(""); return; }
    const qty = qtyNum(cantidad) || 1;
    const variantesActivas = (prod.variantes || []).filter(v => v.activo !== false);
    if (prod.variantes_habilitadas && variantesActivas.length > 1) {
      setVarianteScanPending({ prod, variantesActivas, qty });
      setProductoSeleccionadoId("");
      return;
    }
    const varianteMatch = prod.variantes_habilitadas && variantesActivas.length === 1 ? variantesActivas[0] : null;
    if (addProductoAlCarrito(prod, qty, varianteMatch)) {
      setCantidad("1");
    }
    setProductoSeleccionadoId("");
  };

  // itemKey identifica un renglón único del carrito: mismo producto puede
  // aparecer más de una vez si son variantes distintas (mismo id, distinto
  // variante_id) — sin esto, actualizar/quitar una variante afectaría a todas.
  const itemKey = (item) => `${item.id}::${item.variante_id || ""}`;

  const updateQty = (key, qty) => {
    // Se guarda el texto tal cual se tipea ("0,", "1.5") para poder escribir decimales;
    // al salir del campo se normaliza (ver normalizeQty).
    const clean = decimalQty(qty);
    setItems(prev => prev.map(item => itemKey(item) === key ? { ...item, cantidad: clean } : item));
  };
  const normalizeQty = (key) => {
    setItems(prev => prev.map(item => itemKey(item) === key ? { ...item, cantidad: qtyNum(item.cantidad) || 1 } : item));
  };
  const updateDiscount = (key, pct) => {
    setItems(prev => prev.map(item => itemKey(item) === key ? { ...item, descuento_pct: clampPct(pct) } : item));
  };

  const removeItem = (key) => setItems(prev => prev.filter(item => itemKey(item) !== key));
  const limpiar = () => {
    setNumeroManual("");
    setItems([]);
    setBarcode("");
    setCantidad("1");
    setPagaCon("");
    setClienteId("");
    setCuentaContadoManualId("");
    setDescuentoModo("none");
    setDescuentoGlobalMonto("");
    setDescuentoGlobalPct("");
    setComandaAbierta(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Libera la tarjeta de comanda tras cobrar con éxito (best-effort: si falla,
  // no revierte ni bloquea la venta ya confirmada — solo queda la tarjeta
  // "ocupada" para liberar manualmente desde el Salón).
  const liberarComandaSiCorresponde = async () => {
    if (!comandaAbierta?.id) return;
    try {
      await fetch(`${API}/admin/restaurante/comandas/${comandaAbierta.id}/liberar`, {
        method: "POST",
        headers: jsonHeaders,
      });
    } catch (_e) {
      // silencioso — la venta ya se concretó, esto es solo housekeeping
    }
  };

  const handleSelectCuenta = (id) => {
    setCuentaContadoManualId(id);
    const cta = cuentas.find(c => c.id === id);
    if (cta?.moneda && cta.moneda !== moneda) { setMoneda(cta.moneda); setPagaCon(""); }
  };

  const crearClienteRapido = async () => {
    if (!clienteRapido.nombre.trim()) return toast.error("Ingresá el nombre del cliente");
    try {
      const res = await fetch(`${API}/admin/pdv/clientes`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ ...clienteRapido, logo_tipo: logoTipo, razon_social: clienteRapido.nombre }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "No se pudo crear el cliente");
      const cliente = await res.json();
      setClientes(prev => [cliente, ...prev]);
      setClienteId(cliente.id);
      setClienteRapido({ nombre: "", ruc: "", telefono: "" });
      setShowClienteRapido(false);
      toast.success("Cliente creado y seleccionado");
    } catch (e) {
      toast.error(e.message || "No se pudo crear el cliente");
    }
  };

  const getNumeroFactura = async () => {
    const res = await fetch(`${API}/admin/siguiente-numero-factura/${logoTipo}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ punto_expedicion: puntoActual }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "No se pudo obtener número de factura");
    return res.json();
  };

  // ── Impresión Ticket (mismo modelo que Ventas → imprimirTicket) ─────────────
  const imprimirTicketPDV = (factura, resumen) => {
    const win = window.open("", "_blank", "width=360,height=720");
    if (!win) {
      toast.error("El navegador bloqueó la impresión");
      return;
    }
    const f = factura || {};
    const fmtT = (n) => fmtMonto(n, resumen.moneda);
    const sep = (ch = "=", n = 32) => `<div class="sep">${ch.repeat(n)}</div>`;
    const now = new Date();
    const hora = now.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });

    const emisorNombre = f.emisor_razon_social || activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "";
    const emisorRuc = f.emisor_ruc || activeEmpresaPropia?.ruc || "";
    const emisorDireccion = f.emisor_direccion || activeEmpresaPropia?.direccion || "";
    const emisorTelefono = f.emisor_telefono || activeEmpresaPropia?.telefono || "";
    const emisorEmail = f.emisor_email || activeEmpresaPropia?.email || "";
    const emisorRubro = f.emisor_rubros_dinet || activeEmpresaPropia?.rubros_dinet || "";

    const docNumero = f.numero || "—";
    const fecha = f.fecha || now.toLocaleDateString("es-PY");
    const cajero = f.usuario_nombre || user?.name || user?.email || "Operador";
    const cliNombre = resumen.cliente?.razon_social || resumen.cliente?.nombre || "Consumidor Final";
    const cliRuc = resumen.cliente?.ruc || "—";
    const cliTel = f.cliente_telefono || resumen.cliente?.telefono || "";
    const cliDir = f.direccion || resumen.cliente?.direccion || "";
    const cliEmail = f.cliente_email || resumen.cliente?.email || "";

    const itemsHtml = resumen.items.map(item => `
      <div class="item">
        <div class="item-top">
          <span class="item-cant">${item.cantidad}</span>
          <span class="item-desc">${item.nombre}</span>
          <span class="item-total">${fmtT(item.subtotalDoc)}</span>
        </div>
        ${item.cantidad > 1 ? `<div class="item-sub">${fmtT(item.precioDoc)} c/u</div>` : ""}
        ${item.descuento_pct > 0 ? `<div class="item-sub" style="color:#059669">Desc. ${item.descuento_pct}%</div>` : ""}
      </div>
    `).join("");

    const timbradoHtml = f.nro_timbrado ? `
      <div class="block" style="border:1px dashed #999;padding:4px 6px;margin:4px 0;font-size:10.5px;text-align:center">
        TIMBRADO N°: <b>${f.nro_timbrado}</b>
        ${f.fecha_vigencia_timbrado ? `&nbsp;· Vigencia hasta: ${f.fecha_vigencia_timbrado}` : ""}
        ${f.tipo_emision === "autoimpresor" && f.habilitacion_autoimpresor ? `<br>HABILITACIÓN DE AUTOIMPRESOR N°: <b>${f.habilitacion_autoimpresor}</b>` : ""}
      </div>` : "";

    const subtotal = resumen.totalDoc + (resumen.descuentoDoc || 0);
    const formaPago = resumen.condicion === "contado" ? "Contado" : "Crédito";

    win.document.open();
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Ticket ${docNumero}</title><style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:"Courier New",Courier,monospace;font-size:11px;color:#000;background:#fff;width:300px;margin:0 auto;padding:10px 6px 20px}
      @media print{@page{size:80mm auto;margin:0}body{width:80mm;padding:4px 4px 16px}.no-print{display:none!important}}
      .center{text-align:center}.bold{font-weight:bold}
      .sep{text-align:center;letter-spacing:-0.5px;color:#444;margin:4px 0;font-size:11px;overflow:hidden;white-space:nowrap}
      .block{margin:2px 0}
      .logo-name{font-size:20px;font-weight:900;text-align:center;letter-spacing:1px;line-height:1.1;margin-bottom:2px}
      .empresa-info{font-size:10.5px;text-align:center;line-height:1.6;color:#222}
      .doc-label{font-size:11px;font-weight:bold;text-align:center}
      .doc-num{font-size:13px;font-weight:900;text-align:center}
      .doc-meta{font-size:10px;line-height:1.7;margin-top:2px}
      .cli-row{display:flex;gap:4px;font-size:10.5px;line-height:1.7}
      .cli-label{width:62px;flex-shrink:0;color:#555}
      .cli-val{font-weight:600;flex:1}
      .items-head{display:flex;font-size:10.5px;font-weight:bold;border-bottom:1px solid #000;padding-bottom:2px;margin-bottom:2px}
      .item{margin:2px 0}
      .item-top{display:flex;font-size:10px}
      .item-cant{width:24px;flex-shrink:0;text-align:center}
      .item-desc{flex:1;padding:0 4px;word-break:break-word}
      .item-total{width:68px;flex-shrink:0;text-align:right;font-weight:600}
      .item-sub{font-size:10.5px;color:#555;padding-left:24px;margin-top:-1px}
      .tot-row{display:flex;justify-content:space-between;font-size:10.5px;margin:1px 0}
      .tot-row.big{font-size:14px;font-weight:900;margin-top:4px}
      .tot-row.muted{color:#666;font-size:10.5px}
      .footer{font-size:10.5px;text-align:center;line-height:1.7;margin-top:8px;color:#333}
      .btn-print{display:block;width:100%;margin-top:16px;padding:10px;background:#1d4ed8;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:Arial,sans-serif;font-weight:600}
          *{color:#000!important;font-weight:700!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}.btn-print{color:#fff!important}.item-sub,.cli-label,.tot-row.muted,.footer,.sep{color:#000!important}
    </style></head><body>

      <div class="logo-name">${emisorNombre}</div>
      <div class="empresa-info">
        ${emisorRuc ? `RUC / NIT: ${emisorRuc}<br>` : ""}
        ${emisorDireccion ? `Dirección: ${emisorDireccion}<br>` : ""}
        ${emisorTelefono ? `Teléfono: ${emisorTelefono}<br>` : ""}
        ${emisorEmail ? `${emisorEmail}<br>` : ""}
        ${emisorRubro ? `Rubro: ${emisorRubro}<br>` : ""}
      </div>

      ${sep("=")}
      ${timbradoHtml}

      <div class="doc-label">TICKET FACTURA</div>
      <div class="doc-num">${docNumero}</div>
      <div class="doc-meta center">
        Fecha: ${fecha}&nbsp;&nbsp;Hora: ${hora}<br>
        Cajero: ${cajero}
      </div>

      ${sep("-")}

      <div class="cli-row"><span class="cli-label">Cliente:</span><span class="cli-val">${cliNombre}</span></div>
      <div class="cli-row"><span class="cli-label">RUC/CI:</span><span class="cli-val">${cliRuc}</span></div>
      <div class="cli-row"><span class="cli-label">Dirección:</span><span class="cli-val">${cliDir || "—"}</span></div>
      <div class="cli-row"><span class="cli-label">Tel:</span><span class="cli-val">${cliTel || "—"}</span></div>
      <div class="cli-row"><span class="cli-label">E-mail:</span><span class="cli-val">${cliEmail || "—"}</span></div>

      ${sep("-")}

      <div class="items-head">
        <span style="width:24px;text-align:center">Ct</span>
        <span style="flex:1;padding-left:4px">Descripción</span>
        <span style="width:68px;text-align:right">Total</span>
      </div>
      ${itemsHtml}

      ${sep("-")}

      ${resumen.descuentoDoc > 0 ? `<div class="tot-row muted"><span>SUBTOTAL</span><span>${fmtT(subtotal)}</span></div>` : ""}
      ${resumen.descuentoDoc > 0 ? `<div class="tot-row muted"><span>DESCUENTO</span><span>- ${fmtT(resumen.descuentoDoc)}</span></div>` : ""}

      ${sep("=")}
      <div class="tot-row big"><span>TOTAL A PAGAR</span><span>${fmtT(resumen.totalDoc)}</span></div>
      ${sep("=")}

      ${resumen.moneda !== "PYG" ? `<div class="tot-row muted"><span>Equiv. Gs</span><span>${fmtPYG(resumen.totalPyg)}</span></div><div class="tot-row muted"><span>TC</span><span>1 ${resumen.moneda} = ${fmtPYG(resumen.tcActual).replace("₲ ", "")}</span></div>` : ""}

      <div class="tot-row" style="margin-top:4px"><span>Forma de Pago: ${formaPago}</span></div>
      ${resumen.condicion === "contado" ? `
      <div class="tot-row"><span>Efectivo Recibido</span><span>${fmtT(resumen.efectivo)}</span></div>
      <div class="tot-row"><span>Cambio / Vuelto</span><span>${fmtT(resumen.vuelto)}</span></div>` : ""}

      ${sep("=")}

      <div class="footer">
        ¡Gracias por su compra!
        <br><span style="font-size:7.5px;color:#aaa">Impreso el ${now.toLocaleString("es-PY")}</span>
      </div>

      <button class="btn-print no-print" onclick="window.print()">🖨️ Imprimir Ticket</button>
      <script>setTimeout(() => window.print(), 350);</script>
    </body></html>`);
    win.document.close();
  };

  // ── Impresión A4 (mismo estilo que Ventas → FacturaDocModal) ────────────────
  // A4: usa el MISMO modelo que Ventas → Factura (timbrado, autoimpresor, IVA,
  // monto en letras, tasa de cambio, modelos habilitados por empresa).
  const imprimirFacturaA4PDV = (factura) => {
    setDocPrint({ factura, autoPrint: "a4" });
  };
  // eslint-disable-next-line no-unused-vars
  const imprimirFacturaA4PDVLegacy = (factura, resumen) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("El navegador bloqueó la impresión"); return; }
    const f = factura || {};
    const accentColor = f.logo_tipo === "jar" ? "#dc2626" : f.logo_tipo === "arandu" ? "#2563eb" : "#2563eb";
    const emisorNombre = f.emisor_razon_social || activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "";
    const emisorRuc = f.emisor_ruc || activeEmpresaPropia?.ruc || "";
    const emisorDireccion = f.emisor_direccion || activeEmpresaPropia?.direccion || "";
    const emisorTelefono = f.emisor_telefono || activeEmpresaPropia?.telefono || "";
    const emisorEmail = f.emisor_email || activeEmpresaPropia?.email || "";
    const emisorLogoUrl = f.emisor_logo_url || activeEmpresaPropia?.logo_url || null;
    const docLogo = svgDocumentHeaderLogoHtml(f.logo_tipo, emisorLogoUrl);
    const docRef = f.numero || "—";
    const fmt = (n) => fmtMonto(n, resumen.moneda);

    let subExenta = 0, subGrav5 = 0, subGrav10 = 0;
    resumen.items.forEach(it => {
      const sub = it.subtotalDoc;
      const tipo = it.iva_tipo || "10";
      if (tipo === "exenta") subExenta += sub;
      else if (tipo === "5") subGrav5 += sub;
      else subGrav10 += sub;
    });
    const iva5 = Math.round((subGrav5 / 21) * 100) / 100;
    const iva10 = Math.round((subGrav10 / 11) * 100) / 100;
    const base5 = subGrav5 - iva5;
    const base10 = subGrav10 - iva10;
    const totalIva = iva5 + iva10;
    const montoLetras = resumen.moneda === "PYG"
      ? numToLetras(resumen.totalDoc) + " guaraníes"
      : numToLetras(resumen.totalDoc) + " " + (resumen.moneda || "");

    const itemRows = resumen.items.map((it, i) => {
      const tipo = it.iva_tipo || "10";
      const colExenta = tipo === "exenta" ? fmt(it.subtotalDoc) : "";
      const col5 = tipo === "5" ? fmt(it.subtotalDoc) : "";
      const col10 = tipo !== "exenta" && tipo !== "5" ? fmt(it.subtotalDoc) : "";
      return `<tr style="background:${i % 2 === 0 ? "#fff" : "#fafafa"}">
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:center;font-size:12px">${it.cantidad}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">
          ${it.nombre || ""}
          ${it.descuento_pct > 0 ? `<div style="font-size:9px;color:#059669;margin-top:2px">Descuento aplicado: ${it.descuento_pct}%</div>` : ""}
        </td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${fmt(it.precioDoc)}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${colExenta}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${col5}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px;font-weight:600">${col10}</td>
      </tr>`;
    }).join("");

    const pagadoWatermark = f.estado === "pagada" ? `
      <div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);
        font-size:72px;font-weight:900;color:rgba(22,163,74,0.09);font-family:Arial,sans-serif;
        pointer-events:none;white-space:nowrap;z-index:0;letter-spacing:8px">PAGADO</div>` : "";

    const timbradoBox = `
      <div style="border:2px solid #374151;border-radius:6px;padding:8px 12px;text-align:center;min-width:220px">
        <div style="font-size:9.5px;color:#374151;font-weight:700;text-transform:uppercase;letter-spacing:0.3px">
          TIMBRADO N°: <span style="font-size:13px;color:#111827;font-weight:800">${f.nro_timbrado || "—"}</span>
        </div>
        ${f.fecha_inicio_timbrado ? `<div style="font-size:9px;color:#6b7280;margin-top:2px">Inicio vigencia: ${f.fecha_inicio_timbrado}</div>` : ""}
        ${f.fecha_vigencia_timbrado ? `<div style="font-size:9px;color:#6b7280">Fin vigencia: ${f.fecha_vigencia_timbrado}</div>` : ""}
        <div style="margin-top:6px;border-top:1px solid #d1d5db;padding-top:6px">
          <div style="font-size:18px;font-weight:900;color:#111827;letter-spacing:1px">FACTURA</div>
          <div style="font-size:14px;font-weight:800;color:${accentColor};letter-spacing:0.5px">${docRef}</div>
          ${emisorRuc ? `<div style="font-size:10px;color:#374151;font-weight:700;margin-top:2px">RUC: ${emisorRuc}</div>` : ""}
        </div>
      </div>`;

    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>FACTURA ${docRef}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111827;padding:20px 24px;max-width:860px;margin:0 auto;background:#fff;position:relative;font-size:12px}
        @media print{body{padding:10px 14px}@page{size:A4;margin:0.8cm}}
        .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid ${accentColor};padding-bottom:12px;margin-bottom:10px;gap:16px}
        .company-block{min-width:250px;max-width:330px;flex:0 0 330px}
        .company-info{font-size:10.5px;color:#475569;line-height:1.55;margin-top:6px}
        .client-section{border:1px solid #d1d5db;border-radius:4px;padding:8px 10px;margin-bottom:10px;font-size:11.5px}
        .cl-row{display:flex;gap:0;margin-bottom:3px}
        .cl-label{color:#6b7280;font-size:10px;font-weight:700;text-transform:uppercase;width:130px;flex-shrink:0}
        .cl-val{color:#111827;font-weight:600;border-bottom:1px dotted #d1d5db;flex:1}
        .cl-cond{display:flex;align-items:center;gap:12px;margin-top:6px;padding-top:6px;border-top:1px solid #e5e7eb;font-size:10.5px}
        table.items{width:100%;border-collapse:collapse;font-size:11.5px;margin-bottom:0}
        table.items thead tr{background:${accentColor};color:#fff}
        table.items thead th{padding:6px 8px;font-size:10px;font-weight:700;text-transform:uppercase;border:1px solid rgba(255,255,255,0.15)}
        table.items tfoot td{border:1px solid #d1d5db;padding:5px 8px;font-size:11.5px}
        .sub-row{background:#f8fafc}
        .iva-row{background:#f0fdf4}
        .total-row{background:${accentColor}12;font-weight:800;font-size:13px}
        .footer-note{margin-top:14px;font-size:9.5px;color:#94a3b8;text-align:center;border-top:1px dashed #e2e8f0;padding-top:8px}
      </style>
    </head><body>
      ${pagadoWatermark}
      <div style="position:relative;z-index:1">
        <div class="header">
          <div class="company-block">
            ${docLogo}
            <div class="company-info">
              <div style="font-weight:700;color:#334155">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>
              <div>${emisorDireccion}</div>
              <div>${emisorTelefono ? `Tel: ${emisorTelefono}` : ""}${emisorTelefono && emisorEmail ? " &nbsp;|&nbsp; " : ""}${emisorEmail || ""}</div>
            </div>
          </div>
          <div class="rubro-block"></div>
          ${timbradoBox}
        </div>

        <div class="client-section">
          <div class="cl-row">
            <span class="cl-label">RUC</span>
            <span class="cl-val">${resumen.cliente?.ruc || "&nbsp;"}</span>
            <span class="cl-label" style="margin-left:16px">Fecha</span>
            <span class="cl-val" style="max-width:110px">${f.fecha || today()}</span>
          </div>
          <div class="cl-row">
            <span class="cl-label">Nombre / Razón Social</span>
            <span class="cl-val">${resumen.cliente?.razon_social || resumen.cliente?.nombre || "&nbsp;"}</span>
          </div>
          <div class="cl-cond">
            Condición de venta:
            <span style="display:inline-flex;align-items:center;gap:4px">
              <span style="width:11px;height:11px;border:1.5px solid #374151;display:inline-block;background:${resumen.condicion !== "credito" ? "#374151" : "#fff"}"></span> Contado
            </span>
            <span style="display:inline-flex;align-items:center;gap:4px">
              <span style="width:11px;height:11px;border:1.5px solid #374151;display:inline-block;background:${resumen.condicion === "credito" ? "#374151" : "#fff"}"></span> Crédito
            </span>
          </div>
        </div>

        <table class="items">
          <thead>
            <tr>
              <th style="width:7%;text-align:center">Cant.</th>
              <th style="width:41%;text-align:left">Descripción</th>
              <th style="width:16%;text-align:right">Precio Unit.</th>
              <th style="width:12%;text-align:right">Exentas</th>
              <th style="width:12%;text-align:right">5%</th>
              <th style="width:12%;text-align:right">10%</th>
            </tr>
          </thead>
          <tbody>${itemRows}</tbody>
          <tfoot>
            ${resumen.descuentoDoc > 0 ? `
            <tr class="sub-row">
              <td colspan="5" style="text-align:right;font-weight:700;color:#059669">DESCUENTO</td>
              <td style="text-align:right;font-weight:700;color:#059669">- ${fmt(resumen.descuentoDoc)}</td>
            </tr>` : ""}
            <tr class="iva-row">
              <td colspan="2" style="font-size:10px;color:#374151;font-weight:600">LIQUIDACIÓN DEL IVA</td>
              <td style="text-align:right;font-size:10.5px;color:#374151">5% de ${fmt(base5)}:</td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">${fmt(iva5)}</td>
              <td></td>
            </tr>
            <tr class="iva-row">
              <td colspan="2"></td>
              <td style="text-align:right;font-size:10.5px;color:#374151">10% de ${fmt(base10)}:</td>
              <td></td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">${fmt(iva10)}</td>
            </tr>
            <tr class="sub-row">
              <td colspan="4" style="font-size:10px;color:#374151">
                <strong>Son:</strong> ${montoLetras}
              </td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">TOTAL IVA</td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">${fmt(totalIva)}</td>
            </tr>
            <tr class="total-row">
              <td colspan="4"></td>
              <td style="text-align:right;font-size:12px">TOTAL A PAGAR</td>
              <td style="text-align:right;font-size:14px;color:${accentColor}">${fmt(resumen.totalDoc)}</td>
            </tr>
          </tfoot>
        </table>

        ${resumen.moneda !== "PYG" ? `<div style="margin-top:8px;font-size:10px;color:#6b7280;text-align:right">Equivalente: ${fmtPYG(resumen.totalPyg)} · TC 1 ${resumen.moneda} = ${fmtPYG(resumen.tcActual)}</div>` : ""}
        ${resumen.condicion === "contado" ? `
        <div style="margin-top:8px;display:flex;justify-content:flex-end;gap:24px;font-size:11px;color:#374151">
          <div>Recibido: <strong>${fmt(resumen.efectivo)}</strong></div>
          <div>Vuelto: <strong style="color:#059669">${fmt(resumen.vuelto)}</strong></div>
        </div>` : ""}

        <div class="footer-note">
          ${f.nro_timbrado ? `Timbrado N° ${f.nro_timbrado} &nbsp;·&nbsp;` : ""}
          ${f.logo_tipo?.toUpperCase() || ""} &nbsp;·&nbsp; Impreso el ${new Date().toLocaleDateString("es-PY")}
        </div>
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Decide qué plantilla usar según la configuración (empresa/usuario/preguntar) ──
  const decidirEImprimir = (factura, resumen) => {
    const modo = config.impresion_modo || "global";
    const def = config.impresion_default || "ticket";
    let pref = def;
    if (modo === "por_usuario") pref = userCfg.plantilla_impresion || def;
    if (modo === "preguntar") { setPendingPrint({ factura, resumen }); return; }
    if (pref === "a4") imprimirFacturaA4PDV(factura, resumen);
    else imprimirTicketPDV(factura, resumen);
  };

  const buildResumenVenta = () => ({
    cliente: clienteSeleccionado,
    condicion,
    moneda,
    tcActual,
    totalPyg,
    totalDoc,
    descuentoDoc,
    efectivo,
    comisionPago,
    vuelto,
    items: items.map(item => {
      const cantidadItem = (qtyNum(item.cantidad) || 1);
      const precioPyg = Number(item.precio_venta || 0);
      const brutoPyg = precioPyg * cantidadItem;
      const descuento_pct = clampPct(item.descuento_pct);
      const subtotalPygItem = brutoPyg * (1 - descuento_pct / 100);
      // Prorratea el descuento global proporcionalmente entre ítems para el detalle impreso
      const proporcion = netoTrasItemsPyg > 0 ? subtotalPygItem / netoTrasItemsPyg : 0;
      const subtotalPyg = Math.max(0, subtotalPygItem - (descuentoGlobalPyg * proporcion));
      return {
        ...item,
        cantidad: cantidadItem,
        descuento_pct,
        precioDoc: toDoc(precioPyg),
        subtotalDoc: toDoc(subtotalPyg),
      };
    }),
  });

  const abrirConfirmacion = () => {
    if (!canOperar) return toast.error("Sin permiso para operar PDV");
    if (!items.length) return toast.error("El carrito está vacío");
    if (!clienteSeleccionado) return toast.error("Seleccioná un cliente o configurá un cliente estándar");
    if (!puntoActualData?.timbrado_activo) return toast.error(`Configurá timbrado fiscal para el punto ${puntoActual} antes de usar PDV`);
    if (puntoActualData.vigente === false) return toast.error(`El timbrado del punto ${puntoActual} está vencido o fuera de vigencia (${puntoActualData.timbrado_activo?.fecha_vigencia || "—"})`);
    if (!esAutoNumerico) {
      const nm = numeroManual.trim();
      if (!/^\d{3}-\d{3}-\d{7}$/.test(nm)) return toast.error("Ingresá el número de factura con formato 001-001-0000123");
    }
    if (condicion === "contado" && !cuentaContado) return toast.error("Seleccioná una cuenta de caja para contado");
    if (moneda !== "PYG" && (!tcActual || tcActual <= 0)) return toast.error(`No hay tipo de cambio registrado para ${moneda}`);
    if (condicion === "contado" && efectivo < totalDoc) return toast.error("El monto recibido es menor al total");
    setShowConfirm(true);
  };

  // Recetario agronómico (solo con el módulo habilitado): receta obligatoria con productos controlados
  const [recetaPicker, setRecetaPicker] = useState(null);
  const recetaRef = React.useRef(null);

  const facturar = async () => {
    if (!recetaRef.current) {
      const cantPorProd = new Map();
      for (const it of items) {
        cantPorProd.set(String(it.id), (cantPorProd.get(String(it.id)) || 0) + (qtyNum(it.cantidad) || 1));
      }
      const controlados = await verificarControlados(API, token, logoTipo, [...cantPorProd.keys()]);
      if (controlados.length > 0) {
        setRecetaPicker({ items: controlados.map(c => ({ ...c, cantidad: cantPorProd.get(String(c.producto_id)) || 0 })) });
        return;
      }
    }
    try {
      setFacturando(true);
      const resumen = buildResumenVenta();
      const num = esAutoNumerico ? await getNumeroFactura() : { numero: numeroManual.trim() };
      const conceptos = resumen.items.map(item => ({
        descripcion: item.nombre,
        producto_id: item.id,
        producto_sku: item.sku || "",
        variante_id: item.variante_id || null,
        variante_texto: item.variante_texto || null,
        cantidad: (qtyNum(item.cantidad) || 1),
        precio_unitario: item.precioDoc,
        precio_costo: moneda === "PYG" ? Number(item.precio_costo || 0) : toDoc(Number(item.precio_costo || 0)),
        descuento_pct: item.descuento_pct,
        subtotal: item.subtotalDoc,
        iva_tipo: item.iva_tipo || "10",
      }));
      const payload = {
        logo_tipo: logoTipo,
        tipo: "emitida",
        sin_factura: false,
        numero: num.numero,
        fecha: today(),
        forma_pago: resumen.condicion,
        razon_social: resumen.cliente.razon_social || resumen.cliente.nombre,
        ruc: resumen.cliente.ruc || null,
        empresa_id: resumen.cliente.id,
        empresa_nombre: resumen.cliente.nombre,
        concepto: conceptos.length === 1 ? conceptos[0].descripcion : `${conceptos.length} ítems PDV`,
        conceptos,
        modo: "productos",
        afecta_stock: true,
        deposito_id: depPdv.activo && depositoVenta ? depositoVenta : null,
        monto: resumen.totalDoc,
        total_descuentos: resumen.descuentoDoc,
        moneda: resumen.moneda,
        tipo_cambio: resumen.moneda !== "PYG" ? resumen.tcActual : null,
        estado: resumen.condicion === "contado" ? "pagada" : "pendiente",
        fecha_pago: resumen.condicion === "contado" ? today() : null,
        cuenta_id: resumen.condicion === "contado" ? cuentaContado : null,
        plan_cuenta_id: resumen.condicion === "credito" ? (planCredito || null) : null,
        monto_recibido: resumen.condicion === "contado" ? resumen.efectivo : null,
        procesadora_id: resumen.condicion === "contado" ? (medioCobro.procesadora_id || undefined) : undefined,
        metodo_cobro: resumen.condicion === "contado" ? (medioCobro.metodo_cobro || undefined) : undefined,
        vuelto: resumen.condicion === "contado" ? resumen.vuelto : null,
        punto_expedicion: puntoActual,
        receta: recetaRef.current || undefined,
      };
      recetaRef.current = null;
      const res = await fetch(`${API}/admin/facturas`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "No se pudo crear la factura");
      const factura = await res.json();
      toast.success(`Factura ${factura.numero || num.numero} creada`);
      if (payload.receta && factura?.id && hasPermission?.("recetario.imprimir")) {
        imprimirRecetaDeFactura(API, token, logoTipo, factura.id, activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "");
      }
      await liberarComandaSiCorresponde();
      decidirEImprimir(factura, resumen);
      setShowConfirm(false);
      limpiar();
    } catch (e) {
      toast.error(e.message || "No se pudo facturar");
    } finally {
      setFacturando(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSavingConfig(true);
      const payload = {
        logo_tipo: logoTipo,
        cliente_default_id: config.cliente_default_id || null,
        usuarios: config.usuarios || {},
        monedas_equivalencia: config.monedas_equivalencia || ["USD", "BRL"],
        impresion_modo: config.impresion_modo || "global",
        impresion_default: config.impresion_default || "ticket",
      };
      const res = await fetch(`${API}/admin/pdv/config/${logoTipo}`, {
        method: "PUT",
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "No se pudo guardar configuración");
      setConfig({ ...emptyConfig(logoTipo), ...(await res.json()) });
      toast.success("Configuración PDV guardada");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar configuración");
    } finally {
      setSavingConfig(false);
    }
  };

  const setUserCfg = (uid, patch) => {
    setConfig(prev => ({
      ...prev,
      usuarios: {
        ...(prev.usuarios || {}),
        [uid]: { ...((prev.usuarios || {})[uid] || {}), ...patch },
      },
    }));
  };

  const toggleMonedaEquivalencia = (m) => {
    setConfig(prev => {
      const current = prev.monedas_equivalencia != null ? prev.monedas_equivalencia : ["USD", "BRL"];
      return { ...prev, monedas_equivalencia: current.includes(m) ? current.filter(x => x !== m) : [...current, m] };
    });
  };

  const resumenConfirm = showConfirm ? buildResumenVenta() : null;

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      <div className="sticky top-0 z-20 bg-arandu-dark border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 bg-arandu-dark-light/90 rounded-b-lg">
          <div className="py-2.5 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2.5">
              <Link to="/sistema" className="p-1.5 rounded-lg hover:bg-white/10 text-slate-300"><ArrowLeft className="w-4 h-4" /></Link>
              <h1 className="font-heading text-lg font-bold flex items-center gap-2"><Zap className="w-5 h-5 text-amber-400" /> PDV</h1>
            </div>
            <div className="flex items-center gap-2">
              <EmpresaSwitcher />
              <button onClick={loadData} className="p-1.5 rounded-lg border border-white/10 hover:bg-white/10 text-slate-300" title="Actualizar">
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            {["operacion", ...(canConfig ? ["configuracion"] : [])].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 ${tab === t ? "border-amber-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
                {t === "operacion" ? "Operación" : "Configuración PDV"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {tab === "operacion" && (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_390px] gap-5">
            <section className="bg-white/[0.04] border border-white/10 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-white/10 flex flex-col lg:flex-row gap-3 lg:items-end">
                <div className="flex-1">
                  <label className="text-xs text-slate-400 font-semibold uppercase">Cliente</label>
                  <div className="mt-1">
                    <ClienteSearchSelect
                      clientes={clientes}
                      value={clienteId}
                      onChange={setClienteId}
                      defaultLabel={clientes.find(c => c.id === config.cliente_default_id || c.empresa_id === config.cliente_default_id)?.nombre}
                      onCrearNuevo={(nombre) => { setClienteRapido(v => ({ ...v, nombre })); setShowClienteRapido(true); }}
                    />
                  </div>
                </div>
                {depPdv.activo && depPdv.depositos.length > 0 && (
                  <div>
                    <label className="text-xs text-slate-400 font-semibold uppercase">Depósito</label>
                    <select value={depositoVenta} onChange={e => setDepositoVenta(e.target.value)} style={{ colorScheme: "dark" }}
                      className="mt-1 block bg-slate-900 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white">
                      <option value="">Predeterminado</option>
                      {depPdv.depositos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
                    </select>
                  </div>
                )}
                {canCrearCliente && (
                  <button onClick={() => setShowClienteRapido(v => !v)} className="px-3 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-sm flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Cliente
                  </button>
                )}
              </div>

              {showClienteRapido && (
                <div className="p-4 border-b border-white/10 bg-slate-950/50 grid grid-cols-1 md:grid-cols-[1fr_180px_180px_auto] gap-3 items-end">
                  <div>
                    <label className="text-xs text-slate-400 font-semibold uppercase">Nombre/Razón social</label>
                    <input value={clienteRapido.nombre} onChange={e => setClienteRapido(v => ({ ...v, nombre: e.target.value }))} className="mt-1 w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-semibold uppercase">RUC/C.I.</label>
                    <input value={clienteRapido.ruc} onChange={e => setClienteRapido(v => ({ ...v, ruc: e.target.value }))} className="mt-1 w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-semibold uppercase">Teléfono</label>
                    <input value={clienteRapido.telefono} onChange={e => setClienteRapido(v => ({ ...v, telefono: e.target.value }))} className="mt-1 w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <button onClick={crearClienteRapido} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-sm font-semibold">Crear</button>
                </div>
              )}

              {comandaAbierta && (
                <div className="mx-4 mt-3 px-4 py-2 rounded-lg bg-orange-500/15 border border-orange-400/40 text-orange-200 text-sm flex items-center justify-between">
                  <span>Comanda cargada: <strong>{comandaAbierta.mesa_nombre || comandaAbierta.numero}</strong> — el consumo de la tarjeta se agregó al carrito.</span>
                  <button onClick={() => setComandaAbierta(null)} className="text-orange-300 hover:text-white text-xs underline">Quitar referencia</button>
                </div>
              )}

              <div className="p-4 grid grid-cols-[110px_1fr_auto] gap-3 border-b border-white/10">
                <div>
                  <label className="text-xs text-slate-400 font-semibold uppercase">Cant.</label>
                  <input value={cantidad} inputMode="decimal" onChange={e => setCantidad(decimalQty(e.target.value))} onBlur={() => { if (!qtyNum(cantidad)) setCantidad("1"); }} onFocus={e => e.target.select()} className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-3 text-center text-xl font-bold text-amber-300" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold uppercase">Código de barras</label>
                  <input ref={inputRef} value={barcode} onChange={e => setBarcode(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addProductByBarcode(); }}
                    className="mt-1 w-full bg-slate-950 border border-amber-400/60 rounded-lg px-4 py-3 text-xl font-mono" autoFocus />
                </div>
                <button onClick={addProductByBarcode} className="self-end px-4 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold flex items-center gap-2">
                  <Search className="w-5 h-5" /> Agregar
                </button>
              </div>

              <div className="px-4 pt-3 pb-4 border-b border-white/10">
                <label className="text-xs text-slate-400 font-semibold uppercase">O seleccioná la mercadería del catálogo</label>
                <label className="mt-2 flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
                  <input type="checkbox" checked={soloConStock} onChange={e => { setSoloConStock(e.target.checked); try { localStorage.setItem("pdv_solo_con_stock", e.target.checked ? "1" : "0"); } catch {} }} className="accent-amber-500" />
                  Solo productos con stock (los servicios siempre se muestran)
                </label>
                <div className="mt-1">
                  <ProductoSearchSelect
                    productos={soloConStock ? productos.filter(p => (Number(p.stock_actual) || 0) > 0 || /^\s*servicios?\s*$/i.test(String(p.categoria || "").normalize("NFD").replace(/[\u0300-\u036f]/g, ""))) : productos}
                    value={productoSeleccionadoId}
                    onChange={addProductoSeleccionado}
                    placeholder="Buscar producto por nombre, SKU..."
                    theme="dark"
                  />
                </div>
              </div>

              <div className="overflow-auto max-h-[54vh]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-950/80 sticky top-0 text-slate-400">
                    <tr>
                      <th className="text-left px-4 py-3">Producto</th>
                      <th className="text-right px-4 py-3">Precio</th>
                      <th className="text-right px-4 py-3 w-28">Desc.%</th>
                      <th className="text-center px-4 py-3 w-28">Cant.</th>
                      <th className="text-right px-4 py-3">Total</th>
                      <th className="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={itemKey(item)} className="border-t border-white/5">
                        <td className="px-4 py-3">
                          <p className="font-semibold">{item.nombre}{item.variante_texto ? ` · ${item.variante_texto}` : ""}</p>
                          <p className="text-xs text-slate-500 font-mono">{item.codigo_barras}</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {fmtMonto(toDoc(item.precio_venta), moneda)}
                          {moneda !== "PYG" && <p className="text-[10px] text-slate-500">{fmtPYG(item.precio_venta)}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <input value={item.descuento_pct ?? 0} onChange={e => updateDiscount(itemKey(item), e.target.value)} className="w-20 bg-slate-950 border border-white/10 rounded px-2 py-1 pr-6 text-right" />
                            <Percent className="w-3 h-3 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2" />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input inputMode="decimal" value={item.cantidad} onChange={e => updateQty(itemKey(item), e.target.value)} onBlur={() => normalizeQty(itemKey(item))} onFocus={e => e.target.select()} className="w-20 bg-slate-950 border border-white/10 rounded px-2 py-1 text-center" />
                        </td>
                        <td className="px-4 py-3 text-right font-bold">
                          {fmtMonto(toDoc((Number(item.precio_venta || 0) * qtyNum(item.cantidad)) * (1 - clampPct(item.descuento_pct) / 100)), moneda)}
                        </td>
                        <td className="px-4 py-3"><button onClick={() => removeItem(itemKey(item))} className="p-1 text-slate-500 hover:text-red-300"><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                    {!items.length && (
                      <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-500"><ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-50" />Escaneá productos para iniciar la venta</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="space-y-4">
              <div className="bg-black border border-emerald-400/40 rounded-lg p-5">
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total a facturar</p>
                <div className="text-5xl font-black text-emerald-400 mt-2">{fmtMonto(totalDoc, moneda)}</div>
                {totalDescuentosPyg > 0 && <p className="text-xs text-emerald-300 mt-2">Descuentos: -{fmtMonto(descuentoDoc, moneda)}</p>}
                {moneda !== "PYG" && (
                  <p className="text-xs text-slate-400 mt-2">Equivale a {fmtPYG(totalPyg)} · TC {tcActual ? fmtPYG(tcActual) : "sin cotización"}</p>
                )}
                {equivalenciasTotal.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-slate-400">
                    {equivalenciasTotal.map(e => <span key={e.moneda}>≈ {fmtMonto(e.valor, e.moneda)}</span>)}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">Punto {puntoActual} · {esAutoNumerico ? "numeración automática" : "numeración manual"}</p>
              </div>

              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold">Moneda de factura</p>
                <select value={moneda} onChange={e => { setMoneda(e.target.value); setPagaCon(""); setCuentaContadoManualId(""); }} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {monedasHabilitadas.map(m => <option key={m} value={m}>{m === "PYG" ? "Guaraníes (₲)" : m === "USD" ? "Dólares (USD)" : m === "BRL" ? "Reales (BRL)" : m}</option>)}
                </select>
                {moneda !== "PYG" && (
                  <p className={`text-xs ${tcActual ? "text-emerald-300" : "text-red-300"}`}>
                    {tcActual ? `Cambio automático: 1 ${moneda} = ${fmtPYG(tcActual)}` : `No hay cambio registrado para ${moneda}`}
                    {cotizaciones?.[moneda]?.fecha ? ` (${cotizaciones[moneda].fecha}${cotizaciones[moneda].es_fallback ? " · último disponible" : ""})` : ""}
                  </p>
                )}
              </div>

              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold flex items-center gap-1.5"><Tag className="w-4 h-4 text-rose-300" /> Descuento</p>
                  <div className="flex gap-1">
                    {[["none", "Sin descuento"], ["global", "Global"]].map(([v, l]) => (
                      <button key={v} onClick={() => setDescuentoModo(v)} className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${descuentoModo === v ? "bg-rose-500/20 border-rose-400 text-rose-200" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {descuentoModo === "global" && (
                  <div className="grid grid-cols-[110px_1fr] gap-2 items-end">
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Tipo</label>
                      <select value={descuentoGlobalTipo} onChange={e => setDescuentoGlobalTipo(e.target.value)} className="w-full bg-slate-950 border border-white/10 rounded-lg px-2 py-2 text-sm">
                        <option value="monto">Monto</option>
                        <option value="pct">Porcentaje</option>
                      </select>
                    </div>
                    {descuentoGlobalTipo === "pct" ? (
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Descuento %</label>
                        <input value={descuentoGlobalPct} onChange={e => setDescuentoGlobalPct(e.target.value)} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Ej: 10" />
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Descuento monto ({moneda})</label>
                        <input value={descuentoGlobalMonto} onChange={e => setDescuentoGlobalMonto(moneda === "PYG" ? (numberOnly(e.target.value) ? fmtPYG(numberOnly(e.target.value)) : "") : e.target.value)} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="0" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold">Condición de pago</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setCondicion("contado")} className={`px-3 py-3 rounded-lg border flex items-center justify-center gap-2 ${condicion === "contado" ? "bg-emerald-500/20 border-emerald-400 text-emerald-200" : "border-white/10 text-slate-400"}`}><Banknote className="w-4 h-4" /> Contado</button>
                  <button onClick={() => setCondicion("credito")} className={`px-3 py-3 rounded-lg border flex items-center justify-center gap-2 ${condicion === "credito" ? "bg-cyan-500/20 border-cyan-400 text-cyan-200" : "border-white/10 text-slate-400"}`}><CreditCard className="w-4 h-4" /> Crédito</button>
                </div>
                {condicion === "contado" ? (
                  <>
                    <div>
                      <label className="text-xs text-slate-400 font-semibold uppercase">Cuenta de cobro</label>
                      <select value={cuentaContado} onChange={e => handleSelectCuenta(e.target.value)} className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm">
                        <option value="">Seleccionar cuenta...</option>
                        {cuentas.filter(c => c.activa !== false).map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda || "PYG"})</option>)}
                      </select>
                    </div>
                    <MedioCobroSelector procesadoras={procesadoras} value={medioCobro} onChange={setMedioCobro} monto={totalDoc} fmt={(n) => fmtMonto(n, moneda)} />
                    <input value={pagaCon} onChange={e => setPagaCon(moneda === "PYG" ? (numberOnly(e.target.value) ? fmtPYG(numberOnly(e.target.value)) : "") : e.target.value)} placeholder={`Paga con (${moneda})`} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-3 text-right text-xl font-bold" />
                    <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-emerald-200 uppercase font-bold tracking-wide">Vuelto</span>
                        <strong className="text-2xl text-emerald-300">{fmtMonto(vuelto, moneda)}</strong>
                      </div>
                      {equivalenciasVuelto.length > 0 && (
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-emerald-200/70 justify-end">
                          {equivalenciasVuelto.map(e => <span key={e.moneda}>≈ {fmtMonto(e.valor, e.moneda)}</span>)}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-slate-400">Plan crédito: <span className="text-slate-200">{planCuentas.find(p => p.id === planCredito)?.nombre || "predeterminado de ventas crédito"}</span></p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button onClick={limpiar} className="px-4 py-3 rounded-lg border border-white/10 hover:bg-white/10 text-slate-300">Limpiar</button>
                <button onClick={abrirConfirmacion} disabled={facturando || !canOperar} className="px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 font-bold">
                  {facturando ? "Facturando..." : "Facturar"}
                </button>
              </div>
            </aside>
          </div>
        )}

        {tab === "configuracion" && canConfig && (
          <section className="bg-white/[0.04] border border-white/10 rounded-lg p-5 space-y-5">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-300" />
              <div>
                <h2 className="font-bold text-lg">Configuración PDV</h2>
                <p className="text-sm text-slate-400">Cliente estándar, caja, punto de expedición, monedas de equivalencia e impresión.</p>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-semibold uppercase">Cliente estándar para facturas rápidas</label>
              <select value={config.cliente_default_id || ""} onChange={e => setConfig(v => ({ ...v, cliente_default_id: e.target.value }))} className="mt-1 w-full max-w-xl bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="">Sin cliente estándar</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.ruc ? `${c.ruc} - ` : ""}{c.razon_social || c.nombre}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs text-slate-400 font-semibold uppercase">Monedas de equivalencia (referencia junto al total y al vuelto)</label>
              <div className="flex gap-2 mt-1">
                {["USD", "BRL"].map(m => {
                  const active = (config.monedas_equivalencia != null ? config.monedas_equivalencia : ["USD", "BRL"]).includes(m);
                  return (
                    <button key={m} type="button" onClick={() => toggleMonedaEquivalencia(m)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${active ? "bg-emerald-500/20 border-emerald-400 text-emerald-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border border-white/10 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold flex items-center gap-1.5"><Printer className="w-4 h-4 text-amber-300" /> Impresión de factura</p>
              <div className="flex flex-wrap gap-2">
                {[["global", "Global (misma para todos)"], ["por_usuario", "Por usuario"], ["preguntar", "Preguntar al imprimir"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setConfig(c => ({ ...c, impresion_modo: v }))}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${config.impresion_modo === v ? "bg-amber-500/20 border-amber-400 text-amber-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">
                  {config.impresion_modo === "por_usuario" ? "Plantilla por defecto (si el usuario no tiene una propia):" : config.impresion_modo === "preguntar" ? "Plantilla pre-seleccionada al preguntar:" : "Plantilla para todos:"}
                </span>
                <select value={config.impresion_default || "ticket"} onChange={e => setConfig(c => ({ ...c, impresion_default: e.target.value }))} className="bg-slate-950 border border-white/10 rounded-lg px-2 py-1.5 text-sm">
                  <option value="ticket">Ticket 80mm</option>
                  <option value="a4">A4</option>
                </select>
              </div>
            </div>

            <div className="overflow-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-950 text-slate-400">
                  <tr>
                    <th className="text-left px-4 py-3">Usuario</th>
                    <th className="text-left px-4 py-3">Cuenta contado</th>
                    <th className="text-left px-4 py-3">Plan crédito</th>
                    <th className="text-left px-4 py-3">Punto expedición</th>
                    <th className="text-left px-4 py-3">Plantilla impresión</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map(u => {
                    const cfg = (config.usuarios || {})[u.id] || {};
                    return (
                      <tr key={u.id} className="border-t border-white/5">
                        <td className="px-4 py-3">
                          <p className="font-semibold">{userDisplayName(u)}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <select value={cfg.cuenta_contado_id || ""} onChange={e => setUserCfg(u.id, { cuenta_contado_id: e.target.value })} className="w-full bg-slate-950 border border-white/10 rounded px-2 py-2">
                            <option value="">Cuenta predeterminada por moneda</option>
                            {cuentas.filter(c => c.activa !== false).map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda || "PYG"}){c.es_predeterminada ? " ★" : ""}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select value={cfg.plan_cuenta_credito_id || ""} onChange={e => setUserCfg(u.id, { plan_cuenta_credito_id: e.target.value })} className="w-full bg-slate-950 border border-white/10 rounded px-2 py-2">
                            <option value="">Plan predeterminado crédito</option>
                            {planCuentas.filter(p => p.activa !== false && (!p.uso || p.uso === "venta_credito")).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select value={cfg.punto_expedicion || ""} onChange={e => setUserCfg(u.id, { punto_expedicion: e.target.value })} className="w-full bg-slate-950 border border-white/10 rounded px-2 py-2">
                            <option value="">Primer punto activo</option>
                            {puntos.map(p => <option key={p.codigo} value={p.codigo}>{p.codigo}{p.descripcion ? ` - ${p.descripcion}` : ""}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select value={cfg.plantilla_impresion || ""} onChange={e => setUserCfg(u.id, { plantilla_impresion: e.target.value || null })} disabled={config.impresion_modo !== "por_usuario"} className="w-full bg-slate-950 border border-white/10 rounded px-2 py-2 disabled:opacity-40">
                            <option value="">Usar default de empresa</option>
                            <option value="ticket">Ticket 80mm</option>
                            <option value="a4">A4</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button onClick={saveConfig} disabled={savingConfig} className="px-4 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold flex items-center gap-2">
              <Save className="w-4 h-4" /> {savingConfig ? "Guardando..." : "Guardar configuración"}
            </button>
          </section>
        )}
      </main>

      <RecetaFormModal
        open={!!recetaPicker} onClose={() => setRecetaPicker(null)} token={token} logoTipo={logoTipo}
        cliente={{ id: clienteSeleccionado?.id, nombre: clienteSeleccionado?.razon_social || clienteSeleccionado?.nombre, ruc: clienteSeleccionado?.ruc }}
        items={recetaPicker?.items || []}
        title="Receta agronómica — obligatoria para esta venta"
        confirmLabel="Confirmar receta y facturar"
        puedeCrearIngeniero={!!hasPermission?.("recetario.gestionar_ingenieros")}
        onConfirm={async (payload) => { recetaRef.current = payload; setRecetaPicker(null); facturar(); }}
      />
      {showConfirm && resumenConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !facturando && setShowConfirm(false)} />
          <div className="relative w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Confirmar venta PDV</h3>
                <p className="text-xs text-slate-400">Enter emite, paga si corresponde e imprime el ticket.</p>
              </div>
              <button disabled={facturando} onClick={() => setShowConfirm(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr_240px] gap-4">
              <div className="space-y-4">
                <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                  <p className="text-xs text-slate-400 uppercase font-bold">Cliente</p>
                  <p className="text-white font-semibold mt-1">{resumenConfirm.cliente?.razon_social || resumenConfirm.cliente?.nombre}</p>
                  <p className="text-sm text-slate-400">{resumenConfirm.cliente?.ruc || "Sin RUC"}{resumenConfirm.cliente?.telefono ? ` · ${resumenConfirm.cliente.telefono}` : ""}</p>
                </div>

                {!esAutoNumerico && (
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                    <p className="text-xs text-slate-400 uppercase font-bold">N° de factura (manual)</p>
                    <input value={numeroManual} onChange={e => setNumeroManual(e.target.value.replace(/[^\d-]/g, "").slice(0, 15))} placeholder={`${puntoActualData?.timbrado_activo?.establecimiento || "001"}-${puntoActual}-0000000`} className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-white font-mono" />
                    <p className="text-[11px] text-slate-500 mt-1">El punto {puntoActual} usa numeración manual. Formato 001-001-0000123.</p>
                  </div>
                )}

                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-3 py-2 bg-slate-950 text-xs text-slate-400 uppercase font-bold">Venta</div>
                  <div className="max-h-64 overflow-auto divide-y divide-white/5">
                    {resumenConfirm.items.map(item => (
                      <div key={itemKey(item)} className="px-3 py-2 flex items-start justify-between gap-3 text-sm">
                        <div>
                          <p className="font-semibold">{item.cantidad}x {item.nombre}{item.variante_texto ? ` · ${item.variante_texto}` : ""}</p>
                          <p className="text-xs text-slate-500">
                            {fmtMonto(item.precioDoc, resumenConfirm.moneda)} c/u
                            {item.descuento_pct > 0 ? ` · descuento ${item.descuento_pct}%` : ""}
                          </p>
                        </div>
                        <strong className="text-right">{fmtMonto(item.subtotalDoc, resumenConfirm.moneda)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-4">
                  <p className="text-xs text-emerald-200 uppercase font-bold">Total venta</p>
                  <p className="text-3xl font-black text-emerald-300 mt-1">{fmtMonto(resumenConfirm.totalDoc, resumenConfirm.moneda)}</p>
                  {resumenConfirm.descuentoDoc > 0 && <p className="text-xs text-emerald-200 mt-1">Descuentos: -{fmtMonto(resumenConfirm.descuentoDoc, resumenConfirm.moneda)}</p>}
                  {resumenConfirm.moneda !== "PYG" && <p className="text-xs text-slate-400 mt-2">Equiv. {fmtPYG(resumenConfirm.totalPyg)}</p>}
                </div>

                <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Condición</span><strong>{resumenConfirm.condicion === "contado" ? "Contado" : "Crédito"}</strong></div>
                  <div className="flex justify-between"><span className="text-slate-400">Moneda</span><strong>{resumenConfirm.moneda}</strong></div>
                  {resumenConfirm.moneda !== "PYG" && <div className="flex justify-between"><span className="text-slate-400">Tipo cambio</span><strong>{fmtPYG(resumenConfirm.tcActual)}</strong></div>}
                  {resumenConfirm.condicion === "contado" && (
                    <>
                      <div className="flex justify-between"><span className="text-slate-400">Paga con</span><strong>{fmtMonto(resumenConfirm.efectivo, resumenConfirm.moneda)}</strong></div>
                      <div className="flex justify-between"><span className="text-slate-400">Vuelto</span><strong className="text-emerald-300">{fmtMonto(resumenConfirm.vuelto, resumenConfirm.moneda)}</strong></div>
                    </>
                  )}
                </div>

                <button onClick={facturar} disabled={facturando} className="w-full px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 font-bold flex items-center justify-center gap-2">
                  {facturando ? "Emitiendo..." : <><Printer className="w-4 h-4" /> Emitir e imprimir</>}
                </button>
                <button onClick={() => setShowConfirm(false)} disabled={facturando} className="w-full px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 disabled:opacity-60">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {docPrint && (
        <FacturaDocModal
          factura={docPrint.factura}
          activeEmpresaPropia={activeEmpresaPropia}
          onClose={() => setDocPrint(null)}
          canEdit={false}
          canDelete={false}
          token={token}
          API={API}
          autoPrint={docPrint.autoPrint}
          onAutoPrinted={() => setDocPrint(d => (d ? { ...d, autoPrint: "no" } : d))}
        />
      )}
      {pendingPrint && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPendingPrint(null)} />
          <div className="relative bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-bold text-lg">¿Cómo imprimir la factura?</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { imprimirTicketPDV(pendingPrint.factura, pendingPrint.resumen); setPendingPrint(null); }}
                className={`px-4 py-4 rounded-lg border font-semibold text-sm transition-colors ${(config.impresion_default || "ticket") === "ticket" ? "border-amber-400 bg-amber-500/10 text-amber-200" : "border-white/10 text-slate-300 hover:text-white"}`}>
                🧾 Ticket 80mm
              </button>
              <button
                onClick={() => { imprimirFacturaA4PDV(pendingPrint.factura, pendingPrint.resumen); setPendingPrint(null); }}
                className={`px-4 py-4 rounded-lg border font-semibold text-sm transition-colors ${(config.impresion_default || "ticket") === "a4" ? "border-amber-400 bg-amber-500/10 text-amber-200" : "border-white/10 text-slate-300 hover:text-white"}`}>
                📄 A4
              </button>
            </div>
            <button onClick={() => setPendingPrint(null)} className="w-full px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm">
              Cerrar sin imprimir
            </button>
          </div>
        </div>
      )}

      {/* Selector rápido de variante al escanear un código de barras (o elegir
          del buscador) compartido por más de una variante activa (mismo
          talle/color agrupados bajo un único código de barras a nivel de
          producto). */}
      {varianteScanPending && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Elegí la variante</h3>
                <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">
                  {varianteScanPending.prod.nombre}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setVarianteScanPending(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto p-2">
              {varianteScanPending.variantesActivas.map((v) => {
                const stock = v.stock_actual ?? 0;
                const stockBajo = stock <= 0;
                return (
                  <button
                    key={v.id}
                    type="button"
                    disabled={stockBajo}
                    onClick={() => handleVarianteScanSelect(v)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left transition-colors mb-1 last:mb-0 ${
                      stockBajo
                        ? "opacity-50 cursor-not-allowed bg-gray-50"
                        : "hover:bg-amber-50 border border-transparent hover:border-amber-200"
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {v.atributos_texto || "Variante"}
                    </span>
                    <span className={`text-xs font-semibold whitespace-nowrap ${stockBajo ? "text-red-500" : "text-emerald-600"}`}>
                      Stock: {stock}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <p className="text-[11px] text-gray-400">
                Se agregará al carrito la cantidad indicada de la variante elegida.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
